import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { tokens } from '../../theme';

// ─────────────────────────────────────────────────────────────────────────────
// PageTabs — shared horizontal sub-navigation.
//
// Replaces the 7 secondary sidebars (Course, LearningPath, Content, User,
// Settings, Certificate, Reports) that the old Layout hosted. Each consuming
// page passes its OWN `tabs` array — this component is presentational only and
// does not encode per-area routing logic (that stays where each area defines it).
//
// USAGE:
//   <PageTabs tabs={[
//     { label: 'Details',  to: '/course/123/details' },
//     { label: 'Content',  to: '/course/123/content'  },
//     { label: 'Settings', to: '/course/123/settings', match: '/course/123/settings' },
//   ]} />
//
// Active state: a tab is active when location.pathname starts with `match`
// (defaults to `to`). This mirrors the old Layout's pathname.includes() logic.
//
// MOBILE: horizontally scrollable on narrow screens (no wrap, touch-scroll),
// so a long tab set never breaks the layout. (Full device QA in the mobile pass.)
// ─────────────────────────────────────────────────────────────────────────────

const PageTabs = ({ tabs = [], rightSlot = null }) => {
    const location = useLocation();
    const navigate = useNavigate();

    if (!tabs.length) return null;

    const isActive = (tab) => {
        const m = tab.match || tab.to;
        // Exact match, or a deeper sub-path of m (m + '/...'). NOT a loose
        // startsWith, which would wrongly mark '/users' active on '/users/purge'.
        // For tabs that ARE prefixes of siblings (e.g. '/users' vs '/users/purge'),
        // pass `exact: true` so only the exact path activates them.
        if (tab.exact) return location.pathname === m;
        return location.pathname === m || location.pathname.startsWith(m + '/');
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                borderBottom: `0.5px solid ${tokens.color.borderTertiary}`,
                marginBottom: '24px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: '4px',
                    overflowX: 'auto',
                    flex: 1,
                    // hide scrollbar but keep scrollability on mobile
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {tabs.map((tab) => {
                    const active = isActive(tab);
                    return (
                        <Box
                            key={tab.to}
                            component="button"
                            type="button"
                            onClick={() => (tab.onClick ? tab.onClick() : navigate(tab.to))}
                            sx={{
                                position: 'relative',
                                whiteSpace: 'nowrap',
                                fontFamily: tokens.font.family,
                                fontSize: tokens.font.size.md,
                                fontWeight: active ? tokens.font.weight.medium : tokens.font.weight.regular,
                                color: active ? tokens.color.textPrimary : tokens.color.textSecondary,
                                background: 'transparent',
                                border: 'none',
                                padding: '12px 14px',
                                cursor: 'pointer',
                                transition: `color ${tokens.transition}`,
                                // active underline marker (terracotta — sanctioned active accent)
                                '&::after': active
                                    ? {
                                          content: '""',
                                          position: 'absolute',
                                          left: '14px',
                                          right: '14px',
                                          bottom: '-0.5px',
                                          height: '2px',
                                          background: tokens.color.brand,
                                          borderRadius: '2px 2px 0 0',
                                      }
                                    : {},
                                '&:hover': { color: tokens.color.textPrimary },
                            }}
                        >
                            {tab.label}
                        </Box>
                    );
                })}
            </Box>
            {rightSlot && <Box sx={{ flexShrink: 0 }}>{rightSlot}</Box>}
        </Box>
    );
};

export default PageTabs;
